import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import ProgramForm from "@pages/Program/components/ProgramForm";
import { useForm, FormProvider } from "react-hook-form";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import { ProgramPayload } from "@shared/interfaces/ProgramFiles";
import { toastService } from "@services/ToastService";
import useHandleSaveProgram from "@pages/Program/utils/useHandleSaveProgram";
import { useProgram } from "@pages/Program/utils/useProgram";
import SkeletonProgramForm from "@pages/Program/components/skeleton/SkeletonProgramForm";
import { formatCurrencyValue } from "@pages/Project/utils/utils";
import { useUser } from "@services/UserContext";
import NewItemButton from "@shared/components/NewItemButton";

const EditProgram: React.FC = () => {
  const { user } = useUser();

  const { programId } = useParams();
  const { getProgramById } = useProgram();
  const [submitting, setSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetching, setDataFetching] = useState(true);

  const methods = useForm<ProgramPayload>({
    mode: "onTouched",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      icon: undefined,
      creator_id: -1,
      description: "",
      donors: [],
      members: [],
      budget_spent: "",
      budget: "",
      status: undefined,
      completeion_percentage: undefined,
      start_date: "",
      end_date: "",
    },
  });

  const {
    handleSubmit,
    getValues,
    reset,
    formState: { isValid },
  } = methods;

  const handleSaveProgram = useHandleSaveProgram({ programId }, methods);

  useEffect(() => {
    const getProgram = async () => {
      try {
        if (programId) {
          setIsLoading(true);
          setDataFetching(true);
          const program = await getProgramById(Number(programId));

          if (program) {
            reset({
              ...program,
              donors: program.donors,
              members: program.members.map((m: any) => ({
                user_id: m.user.id,
                leader: m.leader,
              })),
              budget: formatCurrencyValue(program?.budget || ""),
              budget_spent: formatCurrencyValue(program?.budget_spent || ""),
              creator_id: program.creator.id || -1,
            });

            setTimeout(() => {
              setDataFetching(false);
            }, 2000);

            setTimeout(async () => {
              await methods.trigger("members");
            }, 0);
          }
        }
      } catch (error) {
        console.error("Error initializing form:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getProgram();
  }, [programId]);

  const onSave = async (values: ProgramPayload) => {
    if (!isValid || !programId) return;
    setSubmitting(true);

    try {
      const data: ProgramPayload = {
        ...values,
        donors: values.donors,
        reporting_frequency: null,
      };
      await handleSaveProgram(data);
    } catch (error) {
      console.error("Error updating program:", error);
      toastService.error("Failed to update program");
    } finally {
      setSubmitting(false);
    }
  };

  const { blocker, handleProceed, handleCancel, isBlocked } =
    useUnsavedChangesWarning(getValues(), methods.control._defaultValues);

  const editingEnabled = getValues("creator_id")
    ? getValues("creator_id") === user?.id
    : true;

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal
        onProceed={handleProceed}
        onCancel={handleCancel}
        isOpen={isBlocked}
      />

      <form
        onSubmit={handleSubmit(onSave)}
        className="w-full h-[calc(100vh-72px)] flex flex-col relative"
      >
        <div className="flex flex-col h-[calc(100vh-72px)] relative">
          <div className="flex flex-1 px-8">
            <div className="flex-1 flex flex-col">
              <SubTitleHeader
                title="Edit Program"
                spacer={true}
                children={
                  <NewItemButton
                    showIcon={false}
                    type="submit"
                    title="Update"
                    disabled={!isValid || submitting || !editingEnabled}
                    isLoading={submitting}
                  />
                }
              />
              {isLoading ? (
                <SkeletonProgramForm />
              ) : (
                <div className="flex flex-1 pt-5">
                  <div className="flex-1">
                    <ProgramForm
                      isDisabled={!editingEnabled}
                      isEditing={true}
                      editPage={true}
                      dataLoading={dataFetching}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditProgram;
